/**
 * GsplatDissolveTransition - Cross-dissolve effect for gaussian splats
 *
 * Per-splat noise-based dissolve where splats fade in/out individually:
 * - "reveal" mode (mode=1): splats dissolve in, lowest noise first
 * - "hide" mode (mode=-1): splats dissolve out, lowest noise first
 *
 * Used in pairs during splat swaps for a cross-dissolve where both
 * scenes are visible simultaneously and blend through each other.
 *
 * Shader functions use PlayCanvas 2.16+ API:
 * - modifySplatCenter, modifySplatRotationScale, modifySplatColor
 * - gsplatGetSizeFromScale, gsplatMakeSpherical
 */
import * as pc from 'playcanvas';
import { relightingSnippetGLSL, relightingSnippetWGSL } from './gsplat-relighting';
import { mirrorClipSnippetGLSL, mirrorClipSnippetWGSL } from './gsplat-mirror-clip';
import { stepEffectTime, type TransitionClock } from './transition-clock';

const dissolveShaderGLSL = /* glsl */ `
uniform float uDissolveProgress; // 0.0 = start, 1.0 = fully dissolved
uniform float uMode;             // 1.0 = dissolve in (reveal), -1.0 = dissolve out (hide)
uniform vec3 uEdgeTint;          // Color tint at dissolve edge
uniform float uTime;             // Animation time

// Per-splat shared state
float g_visibility;  // 0 = hidden, 1 = visible

// Hash function for per-splat randomization
float hash(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}

void initDissolve(vec3 center) {
    // Each splat gets a random threshold (0-1) based on its position
    float noise = hash(center);
    // Softness controls how gradual each individual splat's transition is
    float softness = 0.08;

    if (uMode > 0.0) {
        // Dissolve in: splats appear as progress exceeds their noise threshold
        g_visibility = smoothstep(noise - softness, noise + softness, uDissolveProgress);
    } else {
        // Dissolve out: splats disappear as progress exceeds their noise threshold
        g_visibility = 1.0 - smoothstep(noise - softness, noise + softness, uDissolveProgress);
    }
}

void modifySplatCenter(inout vec3 center) {
    initDissolve(center);
    rl_casterCenter(center);
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    rl_recordSplatShape(rotation, scale);
    mirrorClipSplat(modifiedCenter, scale);
    if (g_visibility < 0.01) {
        scale = vec3(0.0);
        return;
    }

    if (g_visibility >= 0.99) {
        return;
    }

    // Transition: shrink splats as they dissolve
    float origSize = gsplatGetSizeFromScale(scale);
    float t = g_visibility;

    if (t < 0.2) {
        // Nearly dissolved: tiny spherical dots
        float dotSize = min(t * 0.2 * origSize, origSize);
        gsplatMakeSpherical(scale, dotSize);
    } else {
        scale *= t;
    }
}

void dissolveColorEffect(vec3 center, inout vec4 color) {
    if (g_visibility < 0.01 || g_visibility >= 0.99) return;

    // Tint at the dissolve edge - strongest in the middle of the transition
    float edgeIntensity = sin(g_visibility * 3.14159);
    color.rgb += uEdgeTint * edgeIntensity * 0.25;
}

void modifySplatColor(vec3 center, inout vec4 color) {
    dissolveColorEffect(center, color);
}
`;

const dissolveShaderWGSL = /* wgsl */ `
uniform uDissolveProgress: f32;
uniform uMode: f32;
uniform uEdgeTint: vec3f;
uniform uTime: f32;

var<private> g_visibility: f32;

fn hash(p: vec3f) -> f32 {
    return fract(sin(dot(p, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

fn initDissolve(center: vec3f) {
    let noise = hash(center);
    let softness: f32 = 0.08;

    if (uniform.uMode > 0.0) {
        g_visibility = smoothstep(noise - softness, noise + softness, uniform.uDissolveProgress);
    } else {
        g_visibility = 1.0 - smoothstep(noise - softness, noise + softness, uniform.uDissolveProgress);
    }
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
    initDissolve(*center);
    rl_casterCenter(center);
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    rl_recordSplatShape(*rotation, *scale);
    mirrorClipSplat(modifiedCenter, scale);
    if (g_visibility < 0.01) {
        *scale = vec3f(0.0);
        return;
    }

    if (g_visibility >= 0.99) {
        return;
    }

    let origSize = gsplatGetSizeFromScale(*scale);
    let t = g_visibility;

    if (t < 0.2) {
        let dotSize = min(t * 0.2 * origSize, origSize);
        gsplatMakeSpherical(scale, dotSize);
    } else {
        *scale *= t;
    }
}

fn dissolveColorEffect(center: vec3f, color: ptr<function, vec4f>) {
    if (g_visibility < 0.01 || g_visibility >= 0.99) {
        return;
    }

    let edgeIntensity = sin(g_visibility * 3.14159);
    (*color) = vec4f((*color).rgb + uniform.uEdgeTint * edgeIntensity * 0.25, (*color).a);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    dissolveColorEffect(center, color);
}
`;

// Cache for the script class
let _GsplatDissolveTransitionClass: typeof pc.ScriptType | null = null;

type ScriptTypeWithPrototype<T extends pc.ScriptType> = typeof pc.ScriptType & {
    prototype: T;
};

export interface GsplatDissolveTransitionScript extends pc.ScriptType {
    effectTime: number;
    material: pc.Material | null;
    _effectInitialized: boolean;
    _shadersNeedApplication: boolean;
    _completed: boolean;
    _edgeTintArray: [number, number, number];
    mode: number;           // 1.0 = reveal, -1.0 = hide
    duration: number;       // seconds
    edgeTint: pc.Color;     // color at dissolve edge
    onComplete: (() => void) | null;
    /** Time source; while `clock.manual` is set the engine tick does not advance the dissolve. */
    clock: TransitionClock | null;
    /** Advance by `dtSec` (the video's frame time while the clock is manual). */
    advance: (dtSec: number) => void;
    /** Jump to the end: the final state is written and onComplete runs. */
    finish: () => void;
    _step: (dtSec: number) => void;
    getShaderGLSL: () => string;
    getShaderWGSL: () => string;
    _applyShaders: () => void;
    _applyShaderToMaterial: (material: pc.Material) => void;
    _removeShaders: () => void;
    _updateUniforms: () => void;
    _setUniform: (name: string, value: number | number[] | Float32Array) => void;
}

/**
 * Gets or creates the GsplatDissolveTransition script class.
 * Must be called after PlayCanvas app is initialized.
 */
export function getGsplatDissolveTransitionClass(): typeof pc.ScriptType {
    if (_GsplatDissolveTransitionClass) {
        return _GsplatDissolveTransitionClass;
    }

    const GsplatDissolveTransition = pc.createScript('gsplatDissolveTransition') as ScriptTypeWithPrototype<GsplatDissolveTransitionScript>;

    Object.assign(GsplatDissolveTransition.prototype, {
        effectTime: 0,
        material: null as pc.Material | null,
        _effectInitialized: false,
        _shadersNeedApplication: false,
        _completed: false,
        _edgeTintArray: [0, 0, 0] as [number, number, number],

        // Effect properties
        mode: 1.0,              // 1.0 = dissolve in, -1.0 = dissolve out
        duration: 1.0,          // seconds
        edgeTint: null as pc.Color | null,
        onComplete: null as (() => void) | null,
        clock: null as TransitionClock | null,

        initialize(this: GsplatDissolveTransitionScript) {
            this.effectTime = 0;
            this.material = null;
            this._effectInitialized = false;
            this._shadersNeedApplication = false;
            this._completed = false;
            this._edgeTintArray = [0, 0, 0];

            if (!this.edgeTint) this.edgeTint = new pc.Color(0.3, 0.5, 0.8);

            this.on('enable', () => {
                this.effectTime = 0;
                this._completed = false;
                if (this._effectInitialized) {
                    this._applyShaders();
                } else {
                    this._shadersNeedApplication = true;
                }
            });

            this.on('disable', () => {
                this._removeShaders();
            });

            if (!this.entity.gsplat) {
                return;
            }

            this._effectInitialized = true;
            if (this.enabled) {
                this._applyShaders();
            }
        },

        update(this: GsplatDissolveTransitionScript, dt: number) {
            this._step(this.clock?.manual ? 0 : dt);
        },

        advance(this: GsplatDissolveTransitionScript, dtSec: number) {
            if (this.enabled) this._step(Math.max(0, dtSec));
        },

        finish(this: GsplatDissolveTransitionScript) {
            if (!this.enabled) return;
            this.effectTime = Math.max(this.effectTime, this.duration);
            this._step(0);
            if (this.enabled && !this._completed) {
                // Never got a material to draw with: nothing showed it, so just end it.
                this._completed = true;
                const callback = this.onComplete;
                this.onComplete = null;
                this.enabled = false;
                if (callback) callback();
            }
        },

        _step(this: GsplatDissolveTransitionScript, dt: number) {
            if (!this._effectInitialized) {
                if (this.entity.gsplat) {
                    this._effectInitialized = true;
                    if (this.enabled && this._shadersNeedApplication) {
                        this._applyShaders();
                        if (this.material) {
                            this._shadersNeedApplication = false;
                        }
                    }
                }
                return;
            }

            if (this._shadersNeedApplication) {
                this._applyShaders();
                if (this.material) {
                    this._shadersNeedApplication = false;
                }
            }

            // Self-heal (mirrors gsplat-compare-clip): the live gsplat material can be rebuilt by
            // the engine a frame or two AFTER entity.enabled (PlayCanvas 2.20 non-unified per-entity
            // material rebuild — see createViewer comment ~14966), or have its gsplatModifyVS chunk
            // replaced by another effect. Without re-resolving, a reused/re-enabled entity animates a
            // detached material (renders solid, no fade) — the splat-swap cross-fade asymmetry.
            if (this.enabled) {
                let needsReapply = false;
                const gsplat = this.entity.gsplat;
                if (gsplat) {
                    const isUnified = (gsplat as unknown as { unified?: boolean }).unified === true;
                    const currentMat = isUnified
                        ? ((this.app.scene as unknown as { gsplat?: { material?: pc.Material } }).gsplat?.material ?? null)
                        : gsplat.material;
                    if (currentMat && currentMat !== this.material) {
                        // Material object changed (rebuilt by the engine) → re-bind to the live one.
                        this.material = null;
                        needsReapply = true;
                    } else if (this.material) {
                        // Same material but our chunk was removed/replaced by another effect.
                        const device = this.app.graphicsDevice;
                        const lang = (device as unknown as { isWebGPU?: boolean })?.isWebGPU ? 'wgsl' : 'glsl';
                        const chunk = this.material.getShaderChunks(lang).get('gsplatModifyVS');
                        if (!chunk || !chunk.includes('uDissolveProgress')) {
                            needsReapply = true;
                        }
                    }
                }
                if (needsReapply) {
                    this._shadersNeedApplication = true;
                    this._applyShaders();
                    if (this.material) {
                        this._shadersNeedApplication = false;
                    }
                }
            }

            if (!this.material) return;

            this.effectTime = stepEffectTime(this.effectTime, dt, this.duration);

            const progress = Math.min(this.effectTime / this.duration, 1.0);

            if (progress >= 1.0) {
                // Complete ONCE: write the final state (uDissolveProgress = 1.0) so the splat fully
                // resolves (otherwise it's left at the last sub-1.0 frame), then stop. Guarded so the
                // lingering (relight) script doesn't re-run material.update() every frame.
                if (!this._completed) {
                    this._completed = true;
                    this._updateUniforms();
                    this.material.update();
                    // If relighting is active, stay alive so the combined shader persists.
                    const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
                    if (relightScript && relightScript.enabled) {
                        if (this.onComplete) {
                            const callback = this.onComplete;
                            this.onComplete = null;
                            callback();
                        }
                        return;
                    }
                    const callback = this.onComplete;
                    this.enabled = false;
                    if (callback) callback();
                }
                return;
            }

            this._updateUniforms();
            this.material.update();
        },

        _updateUniforms(this: GsplatDissolveTransitionScript) {
            const progress = Math.min(this.effectTime / this.duration, 1.0);
            this._setUniform('uDissolveProgress', progress);
            this._setUniform('uMode', this.mode);
            this._setUniform('uTime', this.effectTime);
            this._edgeTintArray[0] = this.edgeTint!.r;
            this._edgeTintArray[1] = this.edgeTint!.g;
            this._edgeTintArray[2] = this.edgeTint!.b;
            this._setUniform('uEdgeTint', this._edgeTintArray);
        },

        getShaderGLSL(): string {
            return dissolveShaderGLSL;
        },

        getShaderWGSL(): string {
            return dissolveShaderWGSL;
        },

        _applyShaders(this: GsplatDissolveTransitionScript) {
            const gsplat = this.entity.gsplat;
            if (!gsplat) return;

            if ((gsplat as any).unified) {
                this.material = (this.app.scene as any).gsplat?.material ?? null;
                if (!this.material) {
                    this._shadersNeedApplication = true;
                    return;
                }
                this._applyShaderToMaterial(this.material);
            } else {
                const mat = gsplat.material;
                if (mat) {
                    this.material = mat;
                    this._applyShaderToMaterial(mat);
                } else {
                    this._shadersNeedApplication = true;
                }
            }
        },

        _applyShaderToMaterial(this: GsplatDissolveTransitionScript, material: pc.Material) {
            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
            let shader = lang === 'wgsl' ? this.getShaderWGSL() : this.getShaderGLSL();

            // Check for sibling relighting script - if present, combine shaders
            const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
            if (relightScript && relightScript.enabled) {
                // relightingSnippet already includes mirrorClipSnippet, so no separate prepend needed
                const snippet = lang === 'wgsl' ? relightingSnippetWGSL : relightingSnippetGLSL;
                if (lang === 'wgsl') {
                    shader = snippet + '\n' + shader.replace(
                        /fn modifySplatColor\(center: vec3f, color: ptr<function, vec4f>\) \{\s*dissolveColorEffect\(center, color\);\s*\}/,
                        `fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    applyRelighting(center, color);
    dissolveColorEffect(center, color);
}`
                    );
                } else {
                    shader = snippet + '\n' + shader.replace(
                        /void modifySplatColor\(vec3 center, inout vec4 color\) \{\s*dissolveColorEffect\(center, color\);\s*\}/,
                        `void modifySplatColor(vec3 center, inout vec4 color) {
    applyRelighting(center, color);
    dissolveColorEffect(center, color);
}`
                    );
                }
                relightScript._shaderManagedExternally = true;
            } else {
                // Standalone: prepend mirror clip snippet so mirrorClipSplat() is defined
                const mirrorSnippet = lang === 'wgsl' ? mirrorClipSnippetWGSL : mirrorClipSnippetGLSL;
                shader = mirrorSnippet + shader;
            }

            material.getShaderChunks(lang).set('gsplatModifyVS', shader);
            material.update();
        },

        _removeShaders(this: GsplatDissolveTransitionScript) {
            if (!this.material) return;

            // If relighting is active, hand material back and restore standalone relighting shader
            const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
            if (relightScript && relightScript.enabled) {
                relightScript._shaderManagedExternally = false;
                relightScript.material = this.material;
                const device = this.app.graphicsDevice;
                const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
                const relightShader = lang === 'wgsl' ? relightScript.getShaderWGSL() : relightScript.getShaderGLSL();
                this.material.getShaderChunks(lang).set('gsplatModifyVS', relightShader);
                this.material.update();
                this.material = null;
                return;
            }

            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';

            this.material.getShaderChunks(lang).delete('gsplatModifyVS');
            this.material.update();
            this.material = null;
        },

        _setUniform(this: GsplatDissolveTransitionScript, name: string, value: number | number[] | Float32Array) {
            this.material?.setParameter(name, value);
        },

        destroy(this: GsplatDissolveTransitionScript) {
            this._removeShaders();
        }
    });

    _GsplatDissolveTransitionClass = GsplatDissolveTransition;
    return GsplatDissolveTransition;
}

export const GsplatDissolveTransition = {
    get class() {
        return getGsplatDissolveTransitionClass();
    }
};
